import { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import ChangeContextFields from '@/frontend/features/Fde/ChangeContextFields'
import { FdeEditorDialog } from '@/frontend/features/Fde/FdeEditorSurface'
import { MultiChoice, SelectControl, TextareaControl, TextControl } from '@/frontend/features/Fde/FormControls'
import { fdeRoutes } from '@/frontend/features/Fde/routes'
import type { Evidence, FdeCopy, OntologyClass, OntologyInterface } from '@/frontend/features/Fde/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  ontologyClass: OntologyClass | null
  ontologyClasses: OntologyClass[]
  ontologyInterfaces: OntologyInterface[]
  evidences: Evidence[]
  projectId: string
  copy: FdeCopy
}

export default function OntologyClassDialog({ open, onOpenChange, ontologyClass, ontologyClasses, ontologyInterfaces, evidences, projectId, copy }: Props) {
  const form = useForm({ name: '', description: '', parent_id: '', aliases: '', interface_keys: [] as string[], change_note: '', evidence_id: '' })
  const parentOptions = ontologyClasses
    .filter((candidate) => candidate.id !== ontologyClass?.id)
    .map((candidate) => ({ value: candidate.id, label: `${candidate.family.name} · ${candidate.name}` }))

  useEffect(() => {
    if (!open) return
    form.setData({
      name: ontologyClass?.name ?? '',
      description: ontologyClass?.description ?? '',
      parent_id: ontologyClass?.parent?.id ?? '',
      aliases: ontologyClass?.aliases.join(', ') ?? '',
      interface_keys: ontologyClass?.declared_interface_keys ?? [],
      change_note: '',
      evidence_id: '',
    })
  }, [open, ontologyClass?.id])

  function submit() {
    form.transform((data) => ({
      ontology_class: {
        ...data,
        aliases: data.aliases.split(',').map((value) => value.trim()).filter(Boolean),
      },
    }))
    const options = { preserveScroll: true, onSuccess: () => onOpenChange(false) }
    const query = `?project_id=${projectId}`
    if (ontologyClass) form.patch(`${fdeRoutes.ontologyClasses}/${ontologyClass.id}${query}`, options)
    else form.post(`${fdeRoutes.ontologyClasses}${query}`, options)
  }

  return (
    <FdeEditorDialog
      open={open}
      onOpenChange={onOpenChange}
      title={ontologyClass ? copy.workspace.edit_class : copy.workspace.add_class}
      closeLabel={copy.common.close}
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>{copy.common.cancel}</Button><Button disabled={form.processing || !form.data.name.trim() || !form.data.parent_id} onClick={submit}>{copy.common.save}</Button></>}
      size="standard"
    >
        <div className="grid gap-5 sm:grid-cols-2">
          <TextControl label={copy.forms.name} value={form.data.name} onChange={(event) => form.setData('name', event.target.value)} required />
          <SelectControl label={copy.forms.parent_class} value={form.data.parent_id} onChange={(value) => form.setData('parent_id', value)} options={parentOptions} placeholder={copy.forms.select_parent_class} />
          <div className="sm:col-span-2"><TextareaControl label={copy.forms.description} value={form.data.description} onChange={(event) => form.setData('description', event.target.value)} rows={3} /></div>
          <TextControl label={copy.forms.aliases} value={form.data.aliases} onChange={(event) => form.setData('aliases', event.target.value)} />
          <div className="sm:col-span-2"><MultiChoice label={copy.forms.interfaces} values={form.data.interface_keys} options={ontologyInterfaces.map((item) => ({ id: item.key, name: item.name }))} onChange={(values) => form.setData('interface_keys', values)} /></div>
          <ChangeContextFields className="sm:col-span-2" changeNote={form.data.change_note} evidenceId={form.data.evidence_id} evidences={evidences} copy={copy} onChangeNote={(value) => form.setData('change_note', value)} onEvidenceId={(value) => form.setData('evidence_id', value)} />
        </div>
    </FdeEditorDialog>
  )
}
